import * as pluralize from 'pluralize'
import { VBoolean, Table, TR, TH, TD, Span, VNumber, if as _if, or, not, equal, Div, ElementProperties } from 'alkali'
import app from '../../app/app'
import Icon from 'components/Icon'
import * as deleteIcon from 'icons/delete-1.svg'
import * as cutIcon from 'icons/coupon-cut.svg'
import * as loadingIcon from 'icons/loading-half.svg'
import * as load from 'icons/upload-button.svg'
import * as info from 'icons/information-circle.svg'
import * as note from 'icons/notes-paper-text.svg'
import * as edit from 'icons/code-write.svg'
import * as addition from 'icons/common-file-text-add.svg'
import * as positiveIcon from 'icons/monitor-heart-rate-up.svg'
import * as negativeIcon from 'icons/monitor-heart-rate-down.svg'
import * as _ from 'lodash'
import ValueComposer from 'lib/compose/ValueComposer'
import ExtractionModel from './model/ExtractionModel'
import { ExtractionUpdate } from 'services/extract/types'
import { str } from 'services/extract/util'

export interface ExtractionHistoryOpts extends ElementProperties<ExtractionHistory> {
	extractionModel: ExtractionModel
	readonly?: boolean
	debug?: boolean
}

const summaryFields = summary =>
	['N', 'Designs', 'Acronym'].map(field => ({ field, value: summary?.[field] })).filter(col => col.value)

const nameToLt = name => typeof name == 'string' ? { Name: name, UnboundName: name } : name

const getAllRows = sections => _.flatMap(sections, s => s.Rows || [])

export class ExtractionHistory extends Table {
	created(props: ExtractionHistoryOpts) {
		const { extractionModel, readonly, debug } = props
		const { extractions, loaded } = extractionModel
		const isadmin = app.user?.FeatureConfig?.['Bindings Admin']?.Enabled || app.user?.FeatureConfig?.['Rapid Extract Admin']?.Enabled
		
		//EV-1755 give RE Admin same permissions as SuperAdmin
		const issuper = !!app.user?.Roles?.find(r => r.Name == 'dre_super_admin')
		const renderall = issuper && debug
		const studyCtx = extractionModel.study
		const studyRowsById = _.keyBy(getAllRows(studyCtx.study?.Sections), r => r.Id)
		const deps = studyCtx.study?.metadata?.ExtractDeps
		const groupsById = studyCtx.groupsById
		props.content = extractions.to((extractions: ExtractionUpdate[]) => {
			const refdRows = new Set()
			const refdGroups = new Set()
			extractions.forEach(e =>
				_.forEach(e.Data?.Sections, s =>
					s.Rows?.forEach(r => {
						r.Cells?.forEach(c => {
							c.Group && refdGroups.add(c.Group)
							r.Id && refdRows.add(r.Id)
						})
					})
				)
			)
			return [
				TR([
					TH('Info'),
					TH('User'),
					TH('Type'),
					TH('Name'),
					TH('Group'),
					TH('N'),
					TH('Value'),
					readonly ? undefined : TH()
				]),
				..._.map(_.groupBy(extractions, e => e.ExtractGroup || e.Id), extractions => {
					return extractions.flatMap((extraction: ExtractionUpdate, i) => {
						const fields = summaryFields(extraction.Data?.Summary)
						if (!shouldRenderExtraction(extraction, renderall, fields, refdRows, refdGroups)) return []
						const cellRows = generateExtractionCellRows(extraction, fields, studyRowsById, groupsById, renderall, refdRows, refdGroups)
						return generateExtractionRows(extractionModel, extraction, deps, cellRows, isadmin, issuper, readonly).map(content =>
							[extraction.Id, content] as const
						)
					})
				}).filter(groupEntries => groupEntries.length).flatMap((groupEntries, egroupi) =>
					groupEntries.map(([extractionId, content]) =>
						TR({
							classes: {
								loaded: equal(loaded.property('id'), extractionId),
								'row-even': egroupi % 2 == 0,
								'row-odd': egroupi % 2 != 0,
							}
						}, content)
					)
				)
			]
		})
	}
}

function shouldRenderExtraction(extraction, renderall, fields, refdRows: Set<any>, refdGroups: Set<any>) {
	/* we have content to render for this extraction if
	   - we are rendering all extractions regardless
	   - there is row cell data to render
	   - there is summary data to render
	   - there are rows or groups without data that are not referenced at all
	*/
	const rows = _.flatMap(extraction.Data?.Sections, s => s.Rows || [])
	return renderall ||
		_.some(rows, row => row.Cells?.length) ||
		fields.length ||
		rows.some(r => !refdRows.has(r.Id)) ||
		extraction.Data?.Groups?.some(g => !refdGroups.has(g.Id))
}

const getSectionCells = section => _.flatMap(section?.Rows, r => r.Cells || [])

function generateExtractionCellRows(extraction, fields, studyRowsById, groupsById, renderall, refdRows, refdGroups) {
	const sections = extraction.Data?.Sections || {}
	const groups = extraction.Data?.Groups?.filter(g => renderall || !refdGroups.has(g.Id)) || []
	const rows = []
	rows.push(...buildGroups(groups))
	const sectionRows = _.flatMap(sections, (section, type) =>
		buildSection(type, section.Rows?.filter(row =>
			renderall || row.Cells?.length || !refdRows.has(row.Id)
		).flatMap(row =>
			buildRow(row, studyRowsById[row.Id], (row.Cells?.length ? row.Cells : [{}]).map(cell =>
				buildCell(row, cell, groupsById[cell.Group])
			))
		))
	)
	rows.push(...sectionRows)
	if (fields.length > 0) {
		rows.push(...buildMetadata(extraction.Data?.Summary, fields))
	}
	return rows
}

function generateExtractionRows(extractionModel, extraction, deps, cellRows, isadmin, issuper, readonly) {
	if (!cellRows.length) cellRows = [[TD(), TD(), TD(), TD(), TD()]]
	return wrapCol(
		generateExtractionInfo(extraction, cellRows.length, deps),
		cellRows,
		readonly ? [] : generateExtractionOps(extraction, extractionModel, cellRows.length, deps, isadmin, issuper)
	)
}

const depToStr = d => `${d.extraction || 'Imported'}: ` + _.compact([
	d.groups?.length ? 'Groups: ' + d.groups.join(', ') : undefined,
	d.rows?.length ? 'Rows: ' + d.rows.join(', ') : undefined
]).join('; ')

function generateExtractionInfo(extraction, rowspan, deps) {
	const dep = deps?.[extraction.Id]?.dependencies || []
	const dependencies = dep?.dependencies || []
	// disable deletion if we have not calculated dependencies for these extractions
	const rowTitle = [extraction.Id + ' ' + new Date(extraction.Date).toLocaleString() + ' ' + extraction.User]

	if (extraction.Data?.Summary?.N) rowTitle.push('Study N: ' + extraction.Data.Summary.N)
	if (extraction.Data?.Summary?.Designs) rowTitle.push('Designs: ' + extraction.Data.Summary.Designs.join(', '))
	if (extraction.Data?.Summary?.Acronym) rowTitle.push('Acronym: ' + extraction.Data.Summary.Acronym)

	const extractionNote = /*qualitativeNote ||*/ extraction.Note
	if (extractionNote) rowTitle.push('Note: ' + extractionNote)
	if (dependencies.length) rowTitle.push('Dependencies: \n' + dependencies.map(depToStr).join('\n'))
	return [
		TD({
			attributes: { rowspan },
			title: rowTitle.join('\n')
		}, [
			Div('.ext-hist-cell', [
				Div('.ext-hist-icons', [
					Icon({
						icon: info,
						title: rowTitle.join('\n')
					}),
					...(dep?.isEdit ? [
						Icon({ title: 'Edit', icon: edit }),
						// if there are additions, we are both editing and adding, append the Addition icon
						dep?.isAddition ? Icon({ title: 'Addition', icon: addition }) : undefined
						// otherwise, we are *only* adding, which is assumed so don't display an extract notice
					] : []),
					extractionNote ? Icon({
						icon: note,
						title: extractionNote
					}) : undefined
				])
			])
		]),
		TD('.ext-hist-user', { attributes: { rowspan } }, [
			Div([
				extraction.User,
				extraction.IsCommunity ? Span({ title: extraction.ClientString || 'CE' }, ' CE') : undefined
			]),
			Div([new Date(extraction.Date).toLocaleString()])
		])
	]
}

function generateExtractionOps(extraction, extractionModel, rowspan, deps, isadmin, issuper) {
	const deleteBusy = new VBoolean(false)
	const clearBusy = new VBoolean(false)
	const dep = deps?.[extraction.Id]
	const dependents = (dep?.dependents || []).filter(({groups = [], rows = [],phases = []}) => _.concat(groups, rows, phases).length)
	const hasDeps = !!dependents.length
	const hasCells = !!_.flatMap(extraction.Data?.Sections, section => getSectionCells(section)).length
	/* either we have no dependencies whatsoever, or
	   we have cells and can attempt to split out the cell extraction and delete it
	 */
	const canClear = hasDeps && hasCells
	const canDelete = (!hasDeps || hasCells) && extraction.Loadable
	const reason = !hasDeps ? '' : `:\nDepended upon by ${pluralize('extraction', dependents.length)}:\n` + dependents.map(depToStr).join('\n')
	// disable deletion if we have not calculated dependencies for these extractions
	const deleteDisabled = or(deleteBusy, !canDelete || !deps)
	const canAdmin = isadmin || app.user?.Id == extraction.UserId
	const adminDisabled = or(clearBusy, !canAdmin)
	// see if this extraction actually combined multiple entities we can split
	const canSplit = hasCells || ((+!!extraction.Data?.Sumary + getAllRows(extraction.Data?.Sections).length + (extraction.Data?.Groups || []).length) > 1)
	return [
		TD({ attributes: { rowspan },}, [
			Icon('.clickable', {
				title: extraction.Loadable ? 'Load' : 'Extraction is not accessible to be loaded by current user',
				disabled: or(or(deleteBusy, clearBusy), !extraction.Loadable),
				icon: load,
				classes: {
					disabled: !extraction.Loadable,
					hidden: getSectionCells(extraction.Data?.Sections?.Outcomes).length == 0
				},
				onclick: extraction.Loadable ? e => {
					e.stopPropagation()
					extraction.IsFromHistory = true
					setTimeout(() => {
						extractionModel.reinit(extraction)
					})
				} : undefined
			}),
			Icon({
				title: deleteBusy.to(b =>
					(b ? 'Deleting extraction'
						: (canDelete ? (canClear ? 'Clear' : 'Delete') : 'Cannot delete') +  ` existing ${extraction.Automated ? '' : 'manual '}extraction`) + ' ' + (extraction.Id || extraction.Date)
					+ reason
				),
				disabled: deleteDisabled,
				classes: {
					clickable: not(deleteDisabled),
					disabled: deleteDisabled,
					'spin-icon': deleteBusy,
					hidden: !canAdmin
				},
				icon: _if(deleteBusy, [loadingIcon, deleteIcon]),
				onclick(e) {
					e.stopPropagation()
					if (deleteDisabled.valueOf()) return
					deleteBusy.put(true)
					extractionModel.deleteExtraction(extraction).catch(e => {
						const msg = e?.data?.message
						alert('Error deleting extraction' + (msg ? ': ' + msg : ''))
					}).finally(() => deleteBusy.put(false))
				}
			}),
			(issuper && canSplit) ?
				Icon({
					title: clearBusy.to(b =>
						(b ? 'Splitting extraction'
							: (canAdmin ? 'Split' : 'Cannot split') +  ` existing ${extraction.Automated ? '' : 'manual '}extraction`) + ' ' + (extraction.Id || extraction.Date)
						+ reason
					),
					disabled: adminDisabled,
					classes: {
						clickable: not(adminDisabled),
						disabled: adminDisabled,
						'spin-icon': clearBusy,
						hidden: !canAdmin
					},
					icon: _if(clearBusy, [loadingIcon, cutIcon]),
					onclick(e) {
						e.stopPropagation()
						if (adminDisabled.valueOf()) return
						clearBusy.put(true)
						extractionModel.splitExtraction(extraction).finally(() => clearBusy.put(false))
					}
				}) : undefined
		])
	]
}

const buildCell = (row, cell, group) => [
	TD({ title: group?.Id }, group?.Name || group?.OriginalName || ''),
	TD({ title: cell?.N || group?.N || ''}, cell?.N || group?.N || ''),
	TD({ title: cell?.Id }, cell ? ValueComposer.compose({
		...cell,
		Row: {
			...row,
			Name: nameToLt(row.Name)
		}
	}) : '')
]

const wrapCol = (prepend = [], rows: any[], append = []) => rows.map((row, i) =>
	i == 0 ? [
		...prepend,
		...row,
		...append
	] : row
)

const buildGroups = (groups) => wrapCol([
	TD({ attributes: { rowspan: groups?.length } }, 'Group')
], groups.map(group => [
	TD({ title: group?.Id, attributes: { colspan: 4 } }, group?.Name || group?.OriginalName || ''),
]))

const buildMetadata = (summary, cols) => wrapCol([
	TD({ attributes: { rowspan: cols?.length } }, 'Metadata')
], cols.map(({field, value}) =>[
	TD({ title: field }, field),
	TD({ attributes: { colspan: 3 } }, _.isArray(value) ? value.join(', ') : value),
]))

const buildRow = (row, studyRow, cellRows) => wrapCol([
	TD({ attributes: { rowspan: cellRows.length } }, [
		Div('.ext-hist-info', [
			Div('.ext-hist-outcome', { title: `${row.Type}: ${row.FieldType}` }, [
				row.Name,
				row.IsPositive == null ? undefined :
					Icon({
						title: row.IsPositive == null ? 'Unspecified (assume negative)' : row.IsPositive == true ? 'Positive' : 'Negative',
						icon: row.IsPositive ? positiveIcon : negativeIcon
					}),
				studyRow?.Time?.FollowUp ? ' @' + studyRow.Time.Gestalt : undefined,
				(row.Definition || studyRow.Definition) ? Icon({ icon: note, title: (row.Definition || studyRow.Definition) }) : undefined
			])
		])
	])
], cellRows)

const buildSection = (type, rowRows = []) => wrapCol([
	TD({ attributes: { rowspan: rowRows.length } }, type)
], rowRows)